import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { api } from 'api';
import './_analyticsFilters.scss';
import SingleSelect from 'components/singleSelect/SingleSelect';

const AnalyticsFilters = ({ onFiltersChange, loading }) => {
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    ground_id: '',
    sport_id: '',
    city_id: ''
  });

  const [options, setOptions] = useState({
    grounds: [],
    sports: [],
    cities: []
  });

  const [activeQuickRange, setActiveQuickRange] = useState(null);

  useEffect(() => {
    loadOptions();
  }, []);

  // Detect active quick range when filters change
  useEffect(() => {
    const detectedRange = detectActiveQuickRange();
    setActiveQuickRange(detectedRange);
  }, [filters.start_date, filters.end_date]);

  const loadOptions = async () => {
    try {
      const [groundsRes, sportsRes, citiesRes] = await Promise.all([
        api.searchGrounds(''),
        api.getSports(),
        api.getCities()
      ]);

      const groundResults = Array.isArray(groundsRes) ? groundsRes : (groundsRes?.data || []);
      const sportResults = sportsRes || sportsRes || [];
      const cityResults = citiesRes || citiesRes || [];

      setOptions({
        grounds: groundResults,
        sports: sportResults,
        cities: cityResults
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleSearchGrounds = async (query) => {
    try {
      const res = await api.searchGrounds(query);
      const results = Array.isArray(res) ? res : (res?.data || []);
      setOptions(prev => ({ ...prev, grounds: results }));
    } catch (err) {
      // silent
    }
  };

  // Removed user search

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (type, value) => {
    const newFilters = { ...filters, [type]: value };
    setFilters(newFilters);
    // Clear active range when manually changing dates
    setActiveQuickRange(null);
    onFiltersChange(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      start_date: '',
      end_date: '',
      ground_id: '',
      sport_id: '',
      city: '',
      limit: 10,
      user_id: '',
      search_ground_id: ''
    };
    setFilters(defaultFilters);
    setActiveQuickRange(null);
    onFiltersChange(defaultFilters);
  };

  // Function to get date range for a given range type
  const getDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;

    switch (range) {
      case 'today':
        startDate = endDate = new Date(today);
        break;
      case 'this_week':
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        startDate = thisWeekStart;
        endDate = thisWeekEnd;
        break;
      case 'last_week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        startDate = lastWeekStart;
        endDate = lastWeekEnd;
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_30_days':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = new Date(today);
        break;
      default:
        return null;
    }

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  };

  // Function to detect which quick range matches current filters
  const detectActiveQuickRange = () => {
    if (!filters.start_date || !filters.end_date) {
      return null;
    }

    const ranges = ['today', 'this_week', 'last_week', 'this_month', 'last_month', 'last_30_days'];

    for (const range of ranges) {
      const rangeDates = getDateRange(range);
      if (rangeDates &&
          rangeDates.start_date === filters.start_date &&
          rangeDates.end_date === filters.end_date) {
        return range;
      }
    }

    return null;
  };

  const setQuickDateRange = (range) => {
    const rangeDates = getDateRange(range);
    if (!rangeDates) return;

    const newFilters = {
      ...filters,
      start_date: rangeDates.start_date,
      end_date: rangeDates.end_date
    };
    setFilters(newFilters);
    setActiveQuickRange(range);
    onFiltersChange(newFilters);
  };

  return (
    <div className="analytics-filters">
      <div className="analytics-filters__header">
        <h5>Filters & Date Range</h5>
        <div className="analytics-filters__actions">
          <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </div>

      <Form>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.start_date}
                onChange={(e) => handleDateRangeChange('start_date', e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.end_date}
                onChange={(e) => handleDateRangeChange('end_date', e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Quick Date Ranges</Form.Label>
              <div className="analytics-filters__quick-ranges">
                <Button
                  variant={activeQuickRange === 'today' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('today')}
                  disabled={loading}
                >
                  Today
                </Button>
                <Button
                  variant={activeQuickRange === 'this_week' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('this_week')}
                  disabled={loading}
                >
                  This Week
                </Button>
                <Button
                  variant={activeQuickRange === 'last_week' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_week')}
                  disabled={loading}
                >
                  Last Week
                </Button>
                <Button
                  variant={activeQuickRange === 'this_month' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('this_month')}
                  disabled={loading}
                >
                  This Month
                </Button>
                <Button
                  variant={activeQuickRange === 'last_month' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_month')}
                  disabled={loading}
                >
                  Last Month
                </Button>
                <Button
                  variant={activeQuickRange === 'last_30_days' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_30_days')}
                  disabled={loading}
                >
                  Last 30 Days
                </Button>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Ground</Form.Label>
              <SingleSelect
                options={(options.grounds || []).map(g => ({ value: String(g.id), label: g.name }))}
                value={filters.ground_id ? String(filters.ground_id) : ''}
                onChange={(val) => handleFilterChange('ground_id', val)}
                placeholder="All Grounds"
                isDisabled={loading}
                onSearchChange={handleSearchGrounds}
                searchable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Sport</Form.Label>
              <SingleSelect
                options={(options.sports || []).map(s => ({ value: String(s.id), label: s.name }))}
                value={filters.sport_id ? String(filters.sport_id) : ''}
                onChange={(val) => handleFilterChange('sport_id', val)}
                placeholder="All Sports"
                isDisabled={loading}
                searchable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <SingleSelect
                options={(options.cities || []).map(c => ({ value: String(c.id), label: c.name }))}
                value={filters.city_id ? String(filters.city_id) : ''}
                onChange={(val) => handleFilterChange('city_id', val)}
                placeholder="All Cities"
                isDisabled={loading}
                searchable
              />
            </Form.Group>
          </Col>

        </Row>




      </Form>
    </div>
  );
};

export default AnalyticsFilters;
